class StickyAtc {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.position = el.dataset.position || 'top';

    this.mainForm = document.getElementById(`product-form-${this.sectionId}`);
    this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.mainQtyInput = this.mainForm?.querySelector('.quantity__input');

    if (!this.mainButton || !this.mainForm) return;

    this.variantEl = el.querySelector('[data-sticky-atc-variant]');
    this.priceEl = el.querySelector('[data-sticky-atc-price]');
    this.buttonEl = el.querySelector('[data-sticky-atc-button]');
    this.buttonTextEl = el.querySelector('[data-sticky-atc-button-text]');
    this.spinnerEl = el.querySelector('.loading__spinner');
    this.stickyQtyWrapper = el.querySelector('[data-sticky-atc-qty-input]');
    this.stickyQtyInput = this.stickyQtyWrapper?.querySelector('.quantity__input');
    this.purchaseOptionEl = el.querySelector('[data-sticky-atc-purchase-option]');

    this.setupObserver();
    this.setupQuantitySync();
    this.setupVariantListener();
    this.setupLoadingState();
    this.setupPurchaseOptionSync();
  }

  setupObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const buttonVisible = entry.isIntersecting;
        this.el.classList.toggle('is-visible', !buttonVisible);
        this.el.setAttribute('aria-hidden', buttonVisible ? 'true' : 'false');
        this.updateBackToTopOffset(!buttonVisible);
      },
      { threshold: 0 }
    );
    observer.observe(this.mainButton);
  }

  updateBackToTopOffset(stickyVisible) {
    if (this.position !== 'bottom') return;
    const offset = stickyVisible ? `${this.el.offsetHeight}px` : '0px';
    document.documentElement.style.setProperty('--sticky-atc-offset', offset);
  }

  setupQuantitySync() {
    if (!this.stickyQtyInput || !this.mainQtyInput) return;

    // sticky → main
    this.stickyQtyInput.addEventListener('change', () => {
      this.mainQtyInput.value = this.stickyQtyInput.value;
      this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // main → sticky
    this.mainQtyInput.addEventListener('change', () => {
      this.stickyQtyInput.value = this.mainQtyInput.value;
    });
  }

  setupVariantListener() {
    if (typeof subscribe === 'undefined') return;

    subscribe('variant-change', ({ data }) => {
      if (data.sectionId !== this.sectionId) return;

      const { variant } = data;

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.variantEl) {
        this.variantEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
      }

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }

      // Sync quantity rules
      if (this.stickyQtyInput) {
        this.stickyQtyInput.min = variant.quantity_rule?.min ?? 1;
        this.stickyQtyInput.step = variant.quantity_rule?.increment ?? 1;
        if (variant.quantity_rule?.max) {
          this.stickyQtyInput.max = variant.quantity_rule.max;
        } else {
          this.stickyQtyInput.removeAttribute('max');
        }
        this.stickyQtyInput.value = this.mainQtyInput?.value ?? variant.quantity_rule?.min ?? 1;
      }

      if (variant.available) {
        this.buttonEl?.removeAttribute('disabled');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.buttonEl?.dataset.addText || '';
        }
      } else {
        this.setUnavailable();
      }
    });
  }

  setupLoadingState() {
    this.mainForm.addEventListener('submit', () => {
      if (!this.buttonEl || !this.spinnerEl) return;
      this.buttonEl.setAttribute('aria-disabled', 'true');
      this.buttonEl.classList.add('loading');
      this.spinnerEl.classList.remove('hidden');
    });

    const reset = () => {
      if (!this.buttonEl || !this.spinnerEl) return;
      this.buttonEl.classList.remove('loading');
      this.spinnerEl.classList.add('hidden');
      if (this.buttonEl.getAttribute('aria-disabled') === 'true') {
        this.buttonEl.removeAttribute('aria-disabled');
      }
    };

    if (typeof subscribe !== 'undefined') {
      subscribe('cart-update', reset);
      subscribe('cart-error', reset);
    }
  }

  setupPurchaseOptionSync() {
    if (!this.purchaseOptionEl) return;

    this.boundHandleSellingPlanChanged = (event) => {
      const variantIdInput = this.mainForm.querySelector('input[name="id"]');
      if (
        variantIdInput &&
        event.detail?.variantId &&
        String(variantIdInput.value) !== String(event.detail.variantId)
      ) {
        return;
      }
      this.setPurchaseOption(Boolean(event.detail?.sellingPlanId), event.target);
      this.setPrice(event.detail?.price, event.detail?.priceHtml);
    };
    document.addEventListener('firmhouse.sellingPlanChanged', this.boundHandleSellingPlanChanged);

    const subscriptionContainer = document.querySelector('.selling_plan_app_container');
    if (!subscriptionContainer) return;

    const checkedSubscriptionRadio = subscriptionContainer.querySelector(
      '.subscription-card input[type="radio"][data-radio-type="selling_plan"]:checked'
    );
    if (checkedSubscriptionRadio) {
      this.setPurchaseOption(true, checkedSubscriptionRadio);
      this.setPrice(checkedSubscriptionRadio.dataset.price, checkedSubscriptionRadio.dataset.priceHtml);
    }
  }

  setPurchaseOption(isSubscription, contextEl) {
    if (!this.purchaseOptionEl) return;

    if (!isSubscription) {
      this.purchaseOptionEl.hidden = true;
      this.purchaseOptionEl.textContent = '';
      return;
    }

    const container = contextEl?.closest('.selling_plan_app_container') || document;
    const frequencySelect = container.querySelector('.frequency-select');
    const frequencyText = frequencySelect?.options[frequencySelect.selectedIndex]?.text?.trim();
    const subscriptionLabel = this.purchaseOptionEl.dataset.subscriptionLabel || '';

    this.purchaseOptionEl.textContent = frequencyText
      ? `${subscriptionLabel} · ${frequencyText}`
      : subscriptionLabel;
    this.purchaseOptionEl.hidden = false;
  }

  setPrice(price, priceHtml) {
    if (!this.priceEl) return;
    if (price) {
      this.priceEl.textContent = price;
    } else if (priceHtml) {
      this.priceEl.textContent = priceHtml.replace(/<[^>]*>/g, '').trim();
    }
  }

  setUnavailable() {
    this.buttonEl?.setAttribute('disabled', '');
    if (this.buttonTextEl) {
      this.buttonTextEl.textContent = window.variantStrings?.soldOut || '';
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.moneyFormat || '{{amount_with_comma_separator}}');
    }
    return (cents / 100).toFixed(2);
  }
}

document.querySelectorAll('.sticky-atc').forEach((el) => new StickyAtc(el));
